import { twMerge } from "tailwind-merge";

const Loader = ({ 
  className, 
  size = "md", 
  variant = "spinner", // options: 'spinner', 'dots', 'pulse', 'bars'
  fullScreen = false,
  text = "Loading...",
  overlay = false,
  color = "primary"
}) => {
  const sizeClasses = {
    xs: "h-4 w-4",
    sm: "h-6 w-6", 
    md: "h-8 w-8",
    lg: "h-12 w-12",
    xl: "h-16 w-16"
  };

  const colorClasses = {
    primary: "border-primary",
    secondary: "border-secondary", 
    white: "border-white",
    gray: "border-gray-400"
  };

  const containerClasses = fullScreen 
    ? "fixed inset-0 z-100 flex items-center justify-center bg-black/20 backdrop-blur-sm"
    : overlay
    ? "absolute inset-0 z-100 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-md"
    : "flex items-center justify-center p-4";

  const SpinnerLoader = () => (
    <div className="flex flex-col items-center gap-3">
      <div 
        className={twMerge(
          "rounded-full border-4 border-transparent spinner-rotate",
          sizeClasses[size]
        )}
        style={{
          borderTopColor: color === "primary" ? "#8B4513" : 
                         color === "secondary" ? "#6B7280" :
                         color === "white" ? "white" : "#9CA3AF",
          borderRightColor: color === "primary" ? "#8B4513" : 
                           color === "secondary" ? "#6B7280" :
                           color === "white" ? "white" : "#9CA3AF"
        }}
      />
      {text && (
        <p className={twMerge(
          "text-sm font-medium animate-pulse",
          color === "white" ? "text-white" : "text-gray-600"
        )}>
          {text}
        </p>
      )}
    </div>
  );

  const DotsLoader = () => (
    <div className="flex flex-col items-center gap-3">
      <div className="flex space-x-1">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={twMerge(
              "rounded-full animate-bounce bg-primary",
              size === "xs" ? "h-2 w-2" : 
              size === "sm" ? "h-3 w-3" :
              size === "md" ? "h-4 w-4" :
              size === "lg" ? "h-5 w-5" : "h-6 w-6",
              color === "primary" ? "bg-primary" :
              color === "secondary" ? "bg-secondary" :
              color === "white" ? "bg-white" : "bg-gray-400"
            )}
            style={{
              animationDelay: `${i * 0.1}s`,
              animationDuration: "0.6s"
            }}
          />
        ))}
      </div>
      {text && (
        <p className={twMerge(
          "text-sm font-medium animate-pulse",
          color === "white" ? "text-white" : "text-gray-600"
        )}>
          {text}
        </p>
      )}
    </div>
  );

  const PulseLoader = () => (
    <div className="flex flex-col items-center gap-3">
      <div className={twMerge(
        "rounded-full animate-ping",
        sizeClasses[size],
        color === "primary" ? "bg-primary" :
        color === "secondary" ? "bg-secondary" :
        color === "white" ? "bg-white" : "bg-gray-400"
      )} />
      {text && (
        <p className={twMerge(
          "text-sm font-medium animate-pulse",
          color === "white" ? "text-white" : "text-gray-600"
        )}>
          {text}
        </p>
      )}
    </div>
  );

  const BarsLoader = () => (
    <div className="flex flex-col items-center gap-3">
      <div className="flex space-x-1">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={twMerge(
              "animate-pulse",
              size === "xs" ? "h-4 w-1" :
              size === "sm" ? "h-6 w-1" :
              size === "md" ? "h-8 w-1" :
              size === "lg" ? "h-12 w-2" : "h-16 w-2",
              color === "primary" ? "bg-primary" :
              color === "secondary" ? "bg-secondary" :
              color === "white" ? "bg-white" : "bg-gray-400"
            )}
            style={{
              animationDelay: `${i * 0.15}s`,
              animationDuration: "1s"
            }}
          />
        ))}
      </div>
      {text && (
        <p className={twMerge(
          "text-sm font-medium animate-pulse",
          color === "white" ? "text-white" : "text-gray-600"
        )}>
          {text}
        </p>
      )}
    </div>
  );

  const renderLoader = () => {
    switch (variant) {
      case "dots":
        return <DotsLoader />;
      case "pulse":
        return <PulseLoader />;
      case "bars":
        return <BarsLoader />;
      default:
        return <SpinnerLoader />;
    }
  };

  return (
    <>
      <style>
        {`
          .spinner-rotate {
            animation: spinnerRotate 0.6s linear infinite;
          }
          
          @keyframes spinnerRotate {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
      <div className={twMerge(containerClasses, className)}>
        {renderLoader()}
      </div>
    </>
  );
};

export default Loader;
